"use client";

import { c as _c } from "react/compiler-runtime";
import { type InfiniteData, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, RefreshCcw } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { isErr } from "@intents-explorer/shared-api/result";
import { Price, PriceText, TokenIcon } from "@defuse/ui/price";
import { useTokens } from "@defuse/ui/tokens-provider";
import { QueryFailed } from "@/components/query-failed";
import { RecipientLine } from "@/components/recipient";
import { LinkWithLoader } from "@/components/route-loader";
import { Senders } from "@/components/senders";
import { getIntentLink, getTxLink, Short } from "@/components/short";
import { buttonVariants } from "@defuse/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@defuse/ui/tooltip";
import { usePrevPath } from "@/hooks/usePrevPath";
import { useTabActivity } from "@/hooks/useTabActivity";
import { addDecimalPoint } from "@intents-explorer/shared-api/add-decimal-point";
import { NEAR_ADDRESS_EXPLORER_URL, NEAR_TX_EXPLORER_URL, PRICE_IMPROVEMENT_FEE_RECEIVER } from "@intents-explorer/shared-api/chains";
import { logger } from "@intents-explorer/shared-api/logger";
import { shouldHideAmountsForStatus, statusesToTrack } from "@intents-explorer/shared-api/status";
import { type Tokens, chainLabel } from "@intents-explorer/shared-api/tokens";
import { shouldShowWithdrawTransactions } from "@intents-explorer/shared-api/transactions/transactionHashes";
import { getTransactionCacheKey } from "@intents-explorer/shared-api/transactions/transactionsCommon";
import { type TransactionDetails, getTransaction } from "@/lib/transactions/api/getTransactionByDepositAddress";
import { TRANSACTION_BY_DEPOSIT_ADDRESS_POLLING_INTERVAL_MS } from "@/lib/transactions/polling-intervals";
import type { TransactionsResponse, TransactionItem } from "@intents-explorer/shared-api/transactions/transactionsApiV1";
import { getPriceImprovementFees, type GetPriceImprovementFeesReturnType } from "@/lib/getPriceImprovementFees";
import { pluralize } from "@/lib/pluralize";
import { bigintToStr, numToStr } from "@/lib/type-safe-stringify";
import { Referral } from "../../components/referral";
import { getDepositChain, getRecipientChain, getRefundChain } from "@/lib/transactions/get-transaction-chain";
import { TimestampsVisibleProvider } from "./swap-step-timestamp";
import { TransactionPlan } from "./transaction-plan";
const PRICE_LENGTH = 10;
const HASH_LENGTH = 15;
interface DetailsProps {
  transaction: TransactionDetails;
}
export function Details(t0) {
  const $ = _c(52);
  const {
    transaction: initialTransaction
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  useEffect(_temp, t1);
  const latestReqTimestampRef = useRef(0);
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      latestReqTimestampRef.current = Date.now();
    };
    t3 = [];
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  const isTabActive = useTabActivity();
  const queryClient = useQueryClient();
  const [q, setQ] = useState(initialTransaction);
  let t4;
  let t5;
  if ($[3] !== initialTransaction || $[4] !== queryClient) {
    t4 = () => {
      updateTransactionInCache(queryClient, initialTransaction);
    };
    t5 = [queryClient, initialTransaction];
    $[3] = initialTransaction;
    $[4] = queryClient;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  let t6;
  if ($[7] !== initialTransaction.depositAddress || $[8] !== initialTransaction.depositMemo || $[9] !== initialTransaction.status || $[10] !== isTabActive || $[11] !== queryClient) {
    t6 = () => {
      if (!statusesToTrack.has(initialTransaction.status)) {
        return;
      }
      const intervalId = setInterval(() => {
        if (!isTabActive) {
          return;
        }
        const reqTimestamp = Date.now();
        let newQ;
        (async () => {
          ;
          try {
            newQ = await getTransaction({
              depositAddress: initialTransaction.depositAddress,
              depositMemo: initialTransaction.depositMemo
            });
          } catch (t7) {
            const e = t7;
            logger.error("Polling failed", e);
            return;
          }
          if (reqTimestamp < latestReqTimestampRef.current || newQ === undefined) {
            return;
          }
          setQ(newQ);
          latestReqTimestampRef.current = reqTimestamp;
          updateTransactionInCache(queryClient, newQ);
          if (!statusesToTrack.has(newQ.status)) {
            clearInterval(intervalId);
          }
        })();
      }, TRANSACTION_BY_DEPOSIT_ADDRESS_POLLING_INTERVAL_MS);
      return () => {
        clearInterval(intervalId);
      };
    };
    $[7] = initialTransaction.depositAddress;
    $[8] = initialTransaction.depositMemo;
    $[9] = initialTransaction.status;
    $[10] = isTabActive;
    $[11] = queryClient;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== initialTransaction || $[14] !== isTabActive || $[15] !== queryClient) {
    t7 = [initialTransaction, isTabActive, queryClient];
    $[13] = initialTransaction;
    $[14] = isTabActive;
    $[15] = queryClient;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  useEffect(t6, t7);
  const tokens = useTokens();
  const originChain = tokens[q.originAsset]?.chain;
  const destinationChain = tokens[q.destinationAsset]?.chain;
  const [loadFees, setLoadFees] = useState(false);
  let t8;
  let t9;
  if ($[17] !== q.intentHashes) {
    t8 = ["priceImprovementFees", q.intentHashes];
    t9 = async () => {
      if (q.intentHashes === null) {
        throw new Error("Unreachable");
      }
      const result = await getPriceImprovementFees(q.intentHashes.split(",").filter(Boolean));
      if (isErr(result)) {
        throw new Error("Failed to load price improvement fees", {
          cause: result.err
        });
      }
      return result.ok;
    };
    $[17] = q.intentHashes;
    $[18] = t8;
    $[19] = t9;
  } else {
    t8 = $[18];
    t9 = $[19];
  }
  let t10;
  if ($[20] !== loadFees || $[21] !== t8 || $[22] !== t9) {
    t10 = {
      queryKey: t8,
      queryFn: t9,
      enabled: loadFees,
      retry: false
    };
    $[20] = loadFees;
    $[21] = t8;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const priceImprovementFeesQuery = useQuery(t10);
  let t11;
  if ($[24] !== q) {
    t11 = <Header quote={q} />;
    $[24] = q;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== destinationChain || $[27] !== originChain || $[28] !== q) {
    t12 = <TransactionPlan className="md:col-span-2" quote={q} originChain={originChain} destinationChain={destinationChain} />;
    $[26] = destinationChain;
    $[27] = originChain;
    $[28] = q;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== loadFees || $[31] !== originChain || $[32] !== priceImprovementFeesQuery || $[33] !== q) {
    t13 = <FromSection originChain={originChain} q={q} priceImprovementFeesQuery={priceImprovementFeesQuery} loadFees={loadFees} setLoadFees={setLoadFees} />;
    $[30] = loadFees;
    $[31] = originChain;
    $[32] = priceImprovementFeesQuery;
    $[33] = q;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== destinationChain || $[36] !== loadFees || $[37] !== priceImprovementFeesQuery || $[38] !== q) {
    t14 = <ToSection destinationChain={destinationChain} q={q} priceImprovementFeesQuery={priceImprovementFeesQuery} loadFees={loadFees} setLoadFees={setLoadFees} />;
    $[35] = destinationChain;
    $[36] = loadFees;
    $[37] = priceImprovementFeesQuery;
    $[38] = q;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== destinationChain || $[41] !== originChain || $[42] !== q) {
    t15 = <Hashes q={q} originChain={originChain} destinationChain={destinationChain} />;
    $[40] = destinationChain;
    $[41] = originChain;
    $[42] = q;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] !== t12 || $[45] !== t13 || $[46] !== t14 || $[47] !== t15) {
    t16 = <div className="mb-10 grid grid-cols-1 md:grid-cols-2 md:gap-6">{t12}{t13}{t14}{t15}</div>;
    $[44] = t12;
    $[45] = t13;
    $[46] = t14;
    $[47] = t15;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  let t17;
  if ($[49] !== t11 || $[50] !== t16) {
    t17 = <TimestampsVisibleProvider><div className="mr-auto ml-auto flex w-full max-w-3xl flex-col md:p-4 xl:max-w-5xl">{t11}{t16}</div></TimestampsVisibleProvider>;
    $[49] = t11;
    $[50] = t16;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  return t17;
}
function _temp() {
  window.scrollTo({
    top: 0,
    behavior: "instant"
  });
}
function Header(t0) {
  const $ = _c(17);
  const {
    quote
  } = t0;
  const prevPath = usePrevPath();
  const t1 = prevPath ?? "/";
  let t2;
  let t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = buttonVariants({
      variant: "outline",
      size: "icon",
      className: "hover:from-brand-orange/20 hover:to-brand-yellow/20 transition-colors hover:bg-linear-to-r"
    });
    t3 = <ArrowLeft className="h-4 w-4" />;
    t4 = <span className="sr-only">Back</span>;
    $[0] = t2;
    $[1] = t3;
    $[2] = t4;
  } else {
    t2 = $[0];
    t3 = $[1];
    t4 = $[2];
  }
  let t5;
  if ($[3] !== t1) {
    t5 = <LinkWithLoader href={t1} className={t2}>{t3}{t4}</LinkWithLoader>;
    $[3] = t1;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <h1 className="mt-1 text-lg font-bold md:text-xl">Transaction Details</h1>;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const t7 = quote.referral;
  let t8;
  if ($[6] !== quote.fees) {
    t8 = quote.fees.map(_temp2);
    $[6] = quote.fees;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== quote.referral || $[9] !== t8) {
    t9 = <Referral referral={t7} className="mt-1.5 ml-auto" affiliates={t8} />;
    $[8] = quote.referral;
    $[9] = t8;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== t5 || $[12] !== t9) {
    t10 = <div className="flex w-full items-start gap-2">{t5}{t6}{t9}</div>;
    $[11] = t5;
    $[12] = t9;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <p className="pl-11 text-sm text-gray-600 dark:text-gray-400">Track your swap progress in real-time</p>;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== t10) {
    t12 = <div className="mb-4 flex w-full flex-col gap-1 px-4 md:px-0">{t10}{t11}</div>;
    $[15] = t10;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  return t12;
}
function _temp2(fee) {
  return fee.recipient;
}
interface PriceImprovementProps {
  priceImprovementFeesQuery: UseQueryResult<GetPriceImprovementFeesReturnType>;
  loadFees: boolean;
  setLoadFees: (v: boolean) => void;
}
interface FromSectionProps extends PriceImprovementProps {
  originChain: string | undefined;
  q: TransactionDetails;
}
function FromSection(t0) {
  const $ = _c(37);
  let originChain;
  let priceImprovementProps;
  let q;
  if ($[0] !== t0) {
    ({
      originChain,
      q,
      ...priceImprovementProps
    } = t0);
    $[0] = t0;
    $[1] = originChain;
    $[2] = priceImprovementProps;
    $[3] = q;
  } else {
    originChain = $[1];
    priceImprovementProps = $[2];
    q = $[3];
  }
  let t1;
  if ($[4] !== q.depositTransactions) {
    t1 = Array.from(new Set(q.depositTransactions.flatMap(_temp3)));
    $[4] = q.depositTransactions;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const senders = t1;
  const t2 = q.nearSwapTransactions.length > 0;
  let t3;
  if ($[6] !== q.status || $[7] !== t2) {
    t3 = shouldHideAmountsForStatus(q.status, t2);
    $[6] = q.status;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const shouldHideAmount = t3;
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <h2 className="text-lg font-bold">From</h2>;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== originChain) {
    t5 = originChain === undefined ? undefined : chainLabel(originChain);
    $[10] = originChain;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== q.depositType) {
    t6 = q.depositType === "INTENTS" && <IntentsLabel />;
    $[12] = q.depositType;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t5 || $[15] !== t6) {
    t7 = <div className="mb-2 flex items-baseline gap-2">{t4}<div className="text-base">{t5}{t6}</div></div>;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== originChain || $[18] !== q.depositType || $[19] !== senders) {
    t8 = <div className="mb-6">{senders.length > 0 ? <Senders senders={senders} chain={getDepositChain(q.depositType, originChain)} hashLength={HASH_LENGTH} /> : <span className="text-muted-foreground text-sm">Sender: Unknown</span>}</div>;
    $[17] = originChain;
    $[18] = q.depositType;
    $[19] = senders;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== priceImprovementProps || $[22] !== q || $[23] !== shouldHideAmount) {
    t9 = <PriceImprovementSection q={q} {...priceImprovementProps} side="in" isPriceHidden={shouldHideAmount} />;
    $[21] = priceImprovementProps;
    $[22] = q;
    $[23] = shouldHideAmount;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== q.fees || $[26] !== q.originAsset || $[27] !== shouldHideAmount) {
    t10 = q.fees.length > 0 && <div className="flex flex-col gap-3">{q.fees.map(fee => <div className="text-muted-foreground text-sm" key={fee.recipient}><div className="mb-2">Fee to <Short text={fee.recipient} length={HASH_LENGTH} /></div><Price price={fee.formattedFee} length={PRICE_LENGTH} defuseAssetId={q.originAsset} isPriceHidden={shouldHideAmount} /></div>)}</div>;
    $[25] = q.fees;
    $[26] = q.originAsset;
    $[27] = shouldHideAmount;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== t10 || $[30] !== t9) {
    t11 = <div className="flex flex-col gap-6">{t9}{t10}</div>;
    $[29] = t10;
    $[30] = t9;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <ArrowRight className="bg-background text-muted-foreground absolute top-1/2 -right-[29px] hidden h-8 w-8 -translate-y-1/2 rounded-md border p-1.5 md:block" />;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== t11 || $[34] !== t7 || $[35] !== t8) {
    t13 = <div className="border-border border-ty bg-card relative rounded-none border-x-0 p-6 md:rounded-lg md:border md:border-y dark:bg-transparent">{t7}{t8}{t11}{t12}</div>;
    $[33] = t11;
    $[34] = t7;
    $[35] = t8;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  return t13;
}
function _temp3(tx) {
  return tx.senders;
}
interface ToSectionProps extends PriceImprovementProps {
  destinationChain: string | undefined;
  q: TransactionDetails;
}
function ToSection(t0) {
  const $ = _c(30);
  let destinationChain;
  let priceImprovementProps;
  let q;
  if ($[0] !== t0) {
    ({
      destinationChain,
      q,
      ...priceImprovementProps
    } = t0);
    $[0] = t0;
    $[1] = destinationChain;
    $[2] = priceImprovementProps;
    $[3] = q;
  } else {
    destinationChain = $[1];
    priceImprovementProps = $[2];
    q = $[3];
  }
  const t1 = q.nearSwapTransactions.length > 0;
  let t2;
  if ($[4] !== q.status || $[5] !== t1) {
    t2 = shouldHideAmountsForStatus(q.status, t1);
    $[4] = q.status;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const shouldHideAmount = t2;
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <h2 className="text-lg font-bold">To</h2>;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== destinationChain) {
    t4 = destinationChain === undefined ? undefined : chainLabel(destinationChain);
    $[8] = destinationChain;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== q.recipientType) {
    t5 = q.recipientType === "INTENTS" && <IntentsLabel />;
    $[10] = q.recipientType;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4 || $[13] !== t5) {
    t6 = <div className="mb-2 flex items-baseline gap-2">{t3}<div className="text-base">{t4}{t5}</div></div>;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const t7 = q.recipientType;
  const t8 = q.recipient;
  let t9;
  if ($[15] !== destinationChain || $[16] !== q.recipientType) {
    t9 = getRecipientChain(q.recipientType, destinationChain);
    $[15] = destinationChain;
    $[16] = q.recipientType;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== q.recipient || $[19] !== q.recipientType || $[20] !== t9) {
    t10 = <RecipientLine className="text-muted-foreground mb-6 flex gap-1.5 text-sm" recipientType={t7} recipient={t8} chain={t9} length={HASH_LENGTH} recipientDisplayMode="details" />;
    $[18] = q.recipient;
    $[19] = q.recipientType;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== priceImprovementProps || $[23] !== q || $[24] !== shouldHideAmount) {
    t11 = <div className="flex flex-col gap-6"><PriceImprovementSection q={q} {...priceImprovementProps} side="out" isPriceHidden={shouldHideAmount} /></div>;
    $[22] = priceImprovementProps;
    $[23] = q;
    $[24] = shouldHideAmount;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t10 || $[27] !== t11 || $[28] !== t6) {
    t12 = <div className="border-border bg-card rounded-none border-x-0 border-t p-6 md:rounded-lg md:border md:border-y dark:bg-transparent">{t6}{t10}{t11}</div>;
    $[26] = t10;
    $[27] = t11;
    $[28] = t6;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  return t12;
}
const loadButtonClasses = "self-start whitespace-nowrap rounded-md border border-gray-300/70 bg-white/70 px-2 py-0.5 text-foreground/90 text-sm transition-colors hover:bg-white/80 dark:border-gray-600/70 dark:bg-background/60 dark:text-foreground";
function computePriceImprovementInfo(fees: GetPriceImprovementFeesReturnType | undefined, q: TransactionDetails, side: "in" | "out", tokens: Tokens) {
  const rawAmount = side === "in" ? q.rawAmountIn : q.rawAmountOut;
  const assetId = side === "in" ? q.originAsset : q.destinationAsset;
  if (fees === undefined || fees.length === 0 || rawAmount === null) {
    return null;
  }
  const totalFee = fees.filter(f => {
    return f.tokenId === assetId;
  }).reduce((sum, f) => {
    return sum + BigInt(f.amount);
  }, 0n);
  if (totalFee === 0n) {
    return null;
  }
  const decimals = tokens[assetId]?.decimals ?? 0;
  const rawBigInt = BigInt(rawAmount);
  return {
    originalAmountFormatted: addDecimalPoint(bigintToStr(side === "in" ? rawBigInt + totalFee : rawBigInt - totalFee), decimals),
    improvementFormatted: addDecimalPoint(bigintToStr(totalFee), decimals)
  };
}
function PriceImprovementSection(t0) {
  const $ = _c(24);
  const {
    q,
    priceImprovementFeesQuery,
    loadFees,
    setLoadFees,
    side,
    isPriceHidden
  } = t0;
  const {
    data: fees,
    isLoading,
    error,
    refetch
  } = priceImprovementFeesQuery;
  const tokens = useTokens();
  let t1;
  if ($[0] !== error || $[1] !== fees || $[2] !== isLoading || $[3] !== isPriceHidden || $[4] !== loadFees || $[5] !== q || $[6] !== refetch || $[7] !== setLoadFees || $[8] !== side || $[9] !== tokens) {
    const nonZeroFees = fees?.filter(_temp4);
    const showPriceImprovement = q.mayHavePriceImprovementFees && q.intentHashes !== null && (q.swapType === "EXACT_OUTPUT" ? side === "in" : side === "out");
    let t2;
    if ($[11] !== fees || $[12] !== q || $[13] !== side || $[14] !== tokens) {
      t2 = computePriceImprovementInfo(fees, q, side, tokens);
      $[11] = fees;
      $[12] = q;
      $[13] = side;
      $[14] = tokens;
      $[15] = t2;
    } else {
      t2 = $[15];
    }
    const priceImprovementInfo = t2;
    const defuseAssetId = side === "in" ? q.originAsset : q.destinationAsset;
    const depositOrRecipientType = side === "in" ? q.depositType : q.recipientType;
    const actualAmount = side === "in" ? q.amountInFormatted : q.amountOutFormatted;
    const usdAmount = side === "in" ? q.amountInUsd : q.amountOutUsd;
    let t3;
    if ($[16] !== actualAmount || $[17] !== defuseAssetId || $[18] !== depositOrRecipientType || $[19] !== isPriceHidden || $[20] !== priceImprovementInfo || $[21] !== side || $[22] !== usdAmount) {
      t3 = priceImprovementInfo !== null ? <PriceImprovementBreakdown side={side} {...priceImprovementInfo} defuseAssetId={defuseAssetId} depositOrRecipientType={depositOrRecipientType} originalAmount={priceImprovementInfo.originalAmountFormatted} improvementAmount={priceImprovementInfo.improvementFormatted} actualAmount={actualAmount} usdAmount={usdAmount} /> : <Price price={actualAmount} length={PRICE_LENGTH} defuseAssetId={defuseAssetId} isPriceHidden={isPriceHidden} type={depositOrRecipientType} usdAmount={usdAmount} />;
      $[16] = actualAmount;
      $[17] = defuseAssetId;
      $[18] = depositOrRecipientType;
      $[19] = isPriceHidden;
      $[20] = priceImprovementInfo;
      $[21] = side;
      $[22] = usdAmount;
      $[23] = t3;
    } else {
      t3 = $[23];
    }
    t1 = <>{t3}{showPriceImprovement && <>{!loadFees && <button type="button" onClick={() => {
          setLoadFees(true);
        }} className={loadButtonClasses}>Load price improvement info</button>}{isLoading && <span className={`${loadButtonClasses} inline-flex animate-pulse items-center gap-2`} aria-live="polite">Loading...</span>}{error !== null && <div className="flex flex-col items-start gap-1"><QueryFailed what="price improvement info" correlationId={typeof error.cause === "string" ? error.cause : ""} align="left" /><button type="button" onClick={() => {
            refetch();
          }} className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-sm"><RefreshCcw className="h-3.5 w-3.5" />Retry</button></div>}{nonZeroFees !== undefined && nonZeroFees.length === 0 && <span className="text-muted-foreground text-sm">No price improvement info found</span>}{nonZeroFees !== undefined && nonZeroFees.length > 0 && <div className="flex flex-col gap-3">{nonZeroFees.map((fee_0, index) => {
            const decimals = tokens[fee_0.tokenId]?.decimals ?? 0;
            return <div className="text-muted-foreground text-sm" key={`${fee_0.tokenId}-${numToStr(index)}`}><div className="mb-2">Shared surplus fee to{" "}<Short text={PRICE_IMPROVEMENT_FEE_RECEIVER} length={HASH_LENGTH} link={`${NEAR_ADDRESS_EXPLORER_URL}${PRICE_IMPROVEMENT_FEE_RECEIVER}`} /></div><Price price={addDecimalPoint(fee_0.amount, decimals)} length={PRICE_LENGTH} defuseAssetId={fee_0.tokenId} /></div>;
          })}</div>}</>}</>;
    $[0] = error;
    $[1] = fees;
    $[2] = isLoading;
    $[3] = isPriceHidden;
    $[4] = loadFees;
    $[5] = q;
    $[6] = refetch;
    $[7] = setLoadFees;
    $[8] = side;
    $[9] = tokens;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  return t1;
}
function _temp4(fee) {
  return fee.amount !== "0";
}
function PriceImprovementBreakdown(t0) {
  const $ = _c(38);
  const {
    defuseAssetId,
    depositOrRecipientType,
    originalAmount,
    improvementAmount,
    actualAmount,
    usdAmount,
    side
  } = t0;
  let t1;
  if ($[0] !== side) {
    t1 = side === "out" ? {
      improvement: "Competitive quote improvement surplus",
      sign: "+",
      total: "Total received"
    } : {
      improvement: "Competitive quote savings",
      sign: "\u2212",
      total: "Total paid"
    };
    $[0] = side;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const labels = t1;
  let t2;
  if ($[2] !== defuseAssetId || $[3] !== depositOrRecipientType) {
    t2 = <TokenIcon defuseAssetId={defuseAssetId} type={depositOrRecipientType} />;
    $[2] = defuseAssetId;
    $[3] = depositOrRecipientType;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="text-muted-foreground -mb-0.5 text-[0.625rem] leading-tight font-medium">Original quote</div>;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== defuseAssetId || $[7] !== originalAmount) {
    t4 = <div>{t3}<PriceText price={originalAmount} defuseAssetId={defuseAssetId} length={PRICE_LENGTH} /></div>;
    $[6] = defuseAssetId;
    $[7] = originalAmount;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== labels.improvement) {
    t5 = <div className="text-muted-foreground -mb-0.5 text-[0.625rem] leading-tight font-medium">{labels.improvement}</div>;
    $[9] = labels.improvement;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== labels.sign) {
    t6 = <span className="text-sm">{labels.sign}</span>;
    $[11] = labels.sign;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== defuseAssetId || $[14] !== improvementAmount) {
    t7 = <PriceText price={improvementAmount} defuseAssetId={defuseAssetId} length={PRICE_LENGTH} className="text-green-500" />;
    $[13] = defuseAssetId;
    $[14] = improvementAmount;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t6 || $[17] !== t7) {
    t8 = <div className="flex items-center gap-1 text-green-500">{t6}{t7}</div>;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t5 || $[20] !== t8) {
    t9 = <div>{t5}{t8}</div>;
    $[19] = t5;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== labels.total) {
    t10 = <div className="text-muted-foreground -mb-0.5 text-[0.625rem] leading-tight font-medium">{labels.total}</div>;
    $[22] = labels.total;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== actualAmount || $[25] !== defuseAssetId || $[26] !== usdAmount) {
    t11 = <PriceText price={actualAmount} defuseAssetId={defuseAssetId} length={PRICE_LENGTH} usdAmount={usdAmount} />;
    $[24] = actualAmount;
    $[25] = defuseAssetId;
    $[26] = usdAmount;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== t10 || $[29] !== t11) {
    t12 = <div className="mb-2">{t10}{t11}</div>;
    $[28] = t10;
    $[29] = t11;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== t12 || $[32] !== t4 || $[33] !== t9) {
    t13 = <div className="-mt-[6.5px] flex flex-col gap-3">{t4}{t9}{t12}</div>;
    $[31] = t12;
    $[32] = t4;
    $[33] = t9;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== t13 || $[36] !== t2) {
    t14 = <div className="flex items-start gap-4">{t2}{t13}</div>;
    $[35] = t13;
    $[36] = t2;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  return t14;
}
function Hashes(t0) {
  const $ = _c(70);
  const {
    q,
    originChain,
    destinationChain
  } = t0;
  let t1;
  if ($[0] !== q) {
    t1 = shouldShowWithdrawTransactions(q);
    $[0] = q;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const showWithdraw = t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[2] !== q.depositTransactions || $[3] !== q.depositVaultTransactions || $[4] !== q.intentHashes || $[5] !== q.legacyDestinationChainTxHashes || $[6] !== q.legacyNearTxHashes || $[7] !== q.legacyOriginChainTxHashes || $[8] !== q.nearDepositTransactions || $[9] !== q.nearSwapTransactions || $[10] !== q.passiveWithdrawTransactions || $[11] !== q.refundTransactions || $[12] !== q.withdrawTransactions || $[13] !== showWithdraw) {
    t6 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const intentHashes = q.intentHashes !== null ? q.intentHashes.split(",").filter(Boolean) : [];
      const hasAnyHashes = intentHashes.length > 0 || q.depositTransactions.length > 0 || q.refundTransactions.length > 0 || q.legacyOriginChainTxHashes.length > 0 || showWithdraw && q.withdrawTransactions.length > 0 || q.passiveWithdrawTransactions.length > 0 || q.legacyDestinationChainTxHashes.length > 0 || q.depositVaultTransactions.length > 0 || q.nearDepositTransactions.length > 0 || q.nearSwapTransactions.length > 0 || q.legacyNearTxHashes.length > 0;
      if (!hasAnyHashes) {
        t6 = null;
        break bb0;
      }
      t4 = "border-border bg-card rounded-none border-x-0 border-t p-6 md:col-span-2 md:rounded-lg md:border md:border-y dark:bg-transparent";
      if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
        t5 = <h2 className="mb-6 text-lg font-bold">Transaction Hashes</h2>;
        $[19] = t5;
      } else {
        t5 = $[19];
      }
      t2 = "grid grid-cols-1 gap-6 md:grid-cols-2";
      t3 = intentHashes.length > 0 && <div className="flex flex-col gap-3"><h3 className="text-md font-medium">Intent {pluralize(intentHashes.length, "Hash")}</h3><div className="flex flex-col gap-1.5">{intentHashes.map(_temp5)}</div></div>;
    }
    $[2] = q.depositTransactions;
    $[3] = q.depositVaultTransactions;
    $[4] = q.intentHashes;
    $[5] = q.legacyDestinationChainTxHashes;
    $[6] = q.legacyNearTxHashes;
    $[7] = q.legacyOriginChainTxHashes;
    $[8] = q.nearDepositTransactions;
    $[9] = q.nearSwapTransactions;
    $[10] = q.passiveWithdrawTransactions;
    $[11] = q.refundTransactions;
    $[12] = q.withdrawTransactions;
    $[13] = showWithdraw;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
  } else {
    t2 = $[14];
    t3 = $[15];
    t4 = $[16];
    t5 = $[17];
    t6 = $[18];
  }
  if (t6 !== Symbol.for("react.early_return_sentinel")) {
    return t6;
  }
  let t7;
  if ($[20] !== originChain || $[21] !== q.depositTransactions || $[22] !== q.depositType) {
    t7 = q.depositTransactions.length > 0 && <TransactionHashSection title="Deposit" transactions={q.depositTransactions} chain={getDepositChain(q.depositType, originChain)} />;
    $[20] = originChain;
    $[21] = q.depositTransactions;
    $[22] = q.depositType;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== originChain || $[25] !== q.refundTransactions || $[26] !== q.refundType) {
    t8 = q.refundTransactions.length > 0 && <TransactionHashSection title="Refund" transactions={q.refundTransactions} chain={getRefundChain(q.refundType, originChain)} />;
    $[24] = originChain;
    $[25] = q.refundTransactions;
    $[26] = q.refundType;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] !== originChain || $[29] !== q.legacyOriginChainTxHashes) {
    t9 = q.legacyOriginChainTxHashes.length > 0 && <TransactionHashSection title="Origin Chain" transactions={q.legacyOriginChainTxHashes.map(_temp6)} chain={originChain} />;
    $[28] = originChain;
    $[29] = q.legacyOriginChainTxHashes;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== destinationChain || $[32] !== q.recipientType || $[33] !== q.withdrawTransactions || $[34] !== showWithdraw) {
    t10 = showWithdraw && q.withdrawTransactions.length > 0 && <TransactionHashSection title="Withdraw" transactions={q.withdrawTransactions} chain={getRecipientChain(q.recipientType, destinationChain)} />;
    $[31] = destinationChain;
    $[32] = q.recipientType;
    $[33] = q.withdrawTransactions;
    $[34] = showWithdraw;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  let t11;
  if ($[36] !== destinationChain || $[37] !== q.passiveWithdrawTransactions || $[38] !== q.recipientType) {
    t11 = q.passiveWithdrawTransactions.length > 0 && <TransactionHashSection title="Passive Withdraw" transactions={q.passiveWithdrawTransactions} chain={getRecipientChain(q.recipientType, destinationChain)} />;
    $[36] = destinationChain;
    $[37] = q.passiveWithdrawTransactions;
    $[38] = q.recipientType;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  let t12;
  if ($[40] !== destinationChain || $[41] !== q.legacyDestinationChainTxHashes || $[42] !== q.recipientType) {
    t12 = q.legacyDestinationChainTxHashes.length > 0 && <TransactionHashSection title="Destination Chain" transactions={q.legacyDestinationChainTxHashes.map(_temp7)} chain={getRecipientChain(q.recipientType, destinationChain)} />;
    $[40] = destinationChain;
    $[41] = q.legacyDestinationChainTxHashes;
    $[42] = q.recipientType;
    $[43] = t12;
  } else {
    t12 = $[43];
  }
  let t13;
  if ($[44] !== originChain || $[45] !== q.depositVaultTransactions) {
    t13 = q.depositVaultTransactions.length > 0 && <TransactionHashSection title="Deposit Vault" transactions={q.depositVaultTransactions} chain={originChain} />;
    $[44] = originChain;
    $[45] = q.depositVaultTransactions;
    $[46] = t13;
  } else {
    t13 = $[46];
  }
  let t14;
  if ($[47] !== q.nearDepositTransactions) {
    t14 = q.nearDepositTransactions.length > 0 && <TransactionHashSection title="Near Deposit" transactions={q.nearDepositTransactions} chain="near" />;
    $[47] = q.nearDepositTransactions;
    $[48] = t14;
  } else {
    t14 = $[48];
  }
  let t15;
  if ($[49] !== q.nearSwapTransactions) {
    t15 = q.nearSwapTransactions.length > 0 && <TransactionHashSection title="Near Swap" transactions={q.nearSwapTransactions} chain="near" />;
    $[49] = q.nearSwapTransactions;
    $[50] = t15;
  } else {
    t15 = $[50];
  }
  let t16;
  if ($[51] !== q.legacyNearTxHashes) {
    t16 = q.legacyNearTxHashes.length > 0 && <TransactionHashSection title="NEAR Chain" transactions={q.legacyNearTxHashes.map(_temp8)} chain="near" />;
    $[51] = q.legacyNearTxHashes;
    $[52] = t16;
  } else {
    t16 = $[52];
  }
  let t17;
  if ($[53] !== t10 || $[54] !== t11 || $[55] !== t12 || $[56] !== t13 || $[57] !== t14 || $[58] !== t15 || $[59] !== t16 || $[60] !== t2 || $[61] !== t3 || $[62] !== t7 || $[63] !== t8 || $[64] !== t9) {
    t17 = <div className={t2}>{t3}{t7}{t8}{t9}{t10}{t11}{t12}{t13}{t14}{t15}{t16}</div>;
    $[53] = t10;
    $[54] = t11;
    $[55] = t12;
    $[56] = t13;
    $[57] = t14;
    $[58] = t15;
    $[59] = t16;
    $[60] = t2;
    $[61] = t3;
    $[62] = t7;
    $[63] = t8;
    $[64] = t9;
    $[65] = t17;
  } else {
    t17 = $[65];
  }
  let t18;
  if ($[66] !== t17 || $[67] !== t4 || $[68] !== t5) {
    t18 = <div className={t4}>{t5}{t17}</div>;
    $[66] = t17;
    $[67] = t4;
    $[68] = t5;
    $[69] = t18;
  } else {
    t18 = $[69];
  }
  return t18;
}
function _temp8(txHash_1) {
  return {
    txHash: txHash_1
  };
}
function _temp7(txHash_0) {
  return {
    txHash: txHash_0
  };
}
function _temp6(txHash) {
  return {
    txHash
  };
}
function _temp5(intentHash) {
  return <Short className="text-sm" text={intentHash} key={intentHash} length={HASH_LENGTH} link={getIntentLink(intentHash)} />;
}
interface TransactionHashSectionProps {
  title: string;
  transactions: Array<{
    txHash: string;
    senders?: string[];
  }>;
  chain: string | undefined;
}
function TransactionHashSection(t0) {
  const $ = _c(15);
  const {
    title,
    transactions,
    chain
  } = t0;
  let t1;
  if ($[0] !== transactions.length) {
    t1 = pluralize(transactions.length, "Transaction");
    $[0] = transactions.length;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1 || $[3] !== title) {
    t2 = <h3 className="text-md font-medium">{title} {t1}</h3>;
    $[2] = t1;
    $[3] = title;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== chain || $[6] !== transactions) {
    let t4;
    if ($[8] !== chain) {
      t4 = t5 => {
        const {
          txHash,
          senders: t6
        } = t5;
        const senders = t6 === undefined ? [] : t6;
        const link = chain === "near" ? `${NEAR_TX_EXPLORER_URL}${txHash}` : getTxLink(chain, txHash);
        return <div key={txHash} className="flex flex-wrap items-center gap-x-2 gap-y-1"><Short className="text-sm" text={txHash} length={HASH_LENGTH} link={link} />{senders.length > 0 && <><span className="text-muted-foreground text-sm">Sender{senders.length > 1 ? "s" : ""}:</span>{senders.map(_temp9)}</>}</div>;
      };
      $[8] = chain;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    t3 = transactions.map(t4);
    $[5] = chain;
    $[6] = transactions;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[10] !== t3) {
    t4 = <div className="flex flex-col gap-1.5">{t3}</div>;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t2 || $[13] !== t4) {
    t5 = <div className="flex flex-col gap-3">{t2}{t4}</div>;
    $[12] = t2;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
}
function _temp9(sender) {
  return <span key={sender} className="text-muted-foreground flex items-center text-sm"><Short text={sender} length={HASH_LENGTH} /></span>;
}
function IntentsLabel() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Tooltip><TooltipTrigger asChild={true}><span className="text-muted-foreground tracking-[0.08em cursor-default uppercase">{" "}(INTENTS)</span></TooltipTrigger><TooltipContent>The token is on NEAR Intents contract</TooltipContent></Tooltip>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

/**
 * Updates all query caches that might contain this transaction.
 * This keeps the table view in sync with the details page polling.
 */
function updateTransactionInCache(queryClient: ReturnType<typeof useQueryClient>, updatedTransaction: TransactionDetails) {
  const transactionId = getTransactionCacheKey(updatedTransaction);

  // Convert OneClickDetails to list item format
  // by adding the affiliates field derived from fees
  const transactionForCache: TransactionItem = {
    ...updatedTransaction,
    depositMemo: updatedTransaction.depositMemo ?? null,
    amountInUsd: updatedTransaction.amountInUsd ?? "",
    amountOutUsd: updatedTransaction.amountOutUsd ?? "",
    depositType: updatedTransaction.depositType,
    recipientType: updatedTransaction.recipientType,
    affiliates: updatedTransaction.fees.map(fee => {
      return fee.recipient;
    }),
    quoteTransactions: updatedTransaction.depositTransactions.flatMap(tx => {
      return tx.senders.map(sender => {
        return {
          sender
        };
      });
    }),
    hasNearSwapTransaction: updatedTransaction.nearSwapTransactions.length > 0
  };

  // Update all queries with key pattern ["quotes", ...]
  queryClient.setQueriesData<InfiniteData<TransactionsResponse> | undefined>({
    predicate: query => {
      return Array.isArray(query.queryKey) && query.queryKey[0] === "quotes";
    }
  }, oldData => {
    if (oldData === undefined) {
      return oldData;
    }
    return {
      ...oldData,
      pages: oldData.pages.map(page => {
        return {
          ...page,
          items: page.items.map(tx => {
            if (tx.source !== "swaps" || getTransactionCacheKey(tx) !== transactionId) {
              return tx;
            }
            return transactionForCache;
          })
        };
      })
    };
  });
}